import { useState, useEffect } from "react";
import { Student } from "@/types/models";
import { supabase } from "@/lib/supabase";
import { toast } from "sonner";
import { mapToStudent } from "@/utils/supabaseTypeMappers";
import { useAuth } from "@/hooks/useAuth";
import { studentService } from "@/services/studentService";

export interface EmergencyContact {
  id?: string;
  name: string;
  relationship: string;
  phone: string;
  is_primary: boolean;
}

export interface StudentFormData {
  first_name: string;
  last_name: string;
  preferred_name: string;
  date_of_birth: string;
  gender: string;
  notes: string;
  
  // Contact information
  address_line1: string;
  address_line2: string;
  city: string;
  state: string;
  zip: string;
  phone: string;
  email: string;
  
  // Parent address fields
  uses_parent_address: boolean;
  parent_id_for_address: string;
  
  // Emergency contacts
  emergency_contacts: EmergencyContact[];
  
  // Additional info
  location_id: string;
  profile_picture_url: string;
  start_date: string;
  drop_date: string;
  is_active: boolean;
  no_public_photos: boolean;
}

interface UseStudentFormProps {
  student: Student;
  onUpdated: (student: Student) => void;
}

export const useStudentForm = ({ student, onUpdated }: UseStudentFormProps) => {
  const [formData, setFormData] = useState<StudentFormData>({
    first_name: "",
    last_name: "",
    preferred_name: "",
    date_of_birth: "",
    gender: "",
    notes: "",
    address_line1: "",
    address_line2: "",
    city: "",
    state: "",
    zip: "",
    phone: "",
    email: "",
    uses_parent_address: false,
    parent_id_for_address: "",
    emergency_contacts: [{ name: '', relationship: '', phone: '', is_primary: true }],
    location_id: "",
    profile_picture_url: "",
    start_date: "",
    drop_date: "",
    is_active: true,
    no_public_photos: false
  });
  
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [profilePicture, setProfilePicture] = useState<File | null>(null);
  const { userRole, user } = useAuth();
  const organizationId = user?.user_metadata?.organization_id;

  // Reset form data when student changes
  useEffect(() => {
    if (student) {
      console.log("Updating form data with student:", student.id);
      
      // Fetch emergency contacts if student is loaded
      fetchEmergencyContacts(student.id);
      
      setFormData({
        first_name: student.first_name || "",
        last_name: student.last_name || "",
        preferred_name: student.preferred_name || "",
        date_of_birth: student.date_of_birth || "",
        gender: student.gender || "",
        notes: student.notes || "",
        address_line1: student.address_line1 || "",
        address_line2: student.address_line2 || "",
        city: student.city || "",
        state: student.state || "",
        zip: student.zip || "",
        phone: student.phone || "",
        email: student.email || "",
        uses_parent_address: student.uses_parent_address === true,
        parent_id_for_address: student.parent_id_for_address || "",
        emergency_contacts: [{ name: '', relationship: '', phone: '', is_primary: true }],
        location_id: student.location_id || "",
        profile_picture_url: student.profile_picture_url || "",
        start_date: student.start_date || "",
        drop_date: student.drop_date || "",
        is_active: student.is_active !== false,
        no_public_photos: student.no_public_photos === true
      });
    }
  }, [student]);

  const fetchEmergencyContacts = async (studentId: string) => {
    try {
      const { data, error } = await supabase
        .from('emergency_contacts')
        .select('*')
        .eq('student_id', studentId)
        .order('is_primary', { ascending: false });
      
      if (error) throw error;
      
      if (data && data.length > 0) {
        // Filter out empty contacts - only use contacts with actual data
        const validContacts = data.filter(contact => 
          (contact.name && contact.name.trim()) || 
          (contact.relationship && contact.relationship.trim()) || 
          (contact.phone && contact.phone.trim())
        );

        if (validContacts.length > 0) {
          setFormData(prev => ({
            ...prev,
            emergency_contacts: validContacts.map(contact => ({
              id: contact.id,
              name: contact.name || '',
              relationship: contact.relationship || '',
              phone: contact.phone || '',
              is_primary: contact.is_primary || false
            }))
          }));
        }
      }
    } catch (err) {
      console.error("Error fetching emergency contacts:", err);
      // If there's an error fetching contacts, we'll use the default empty one
    }
  };

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleGenderChange = (value: string) => {
    setFormData((prev) => ({
      ...prev,
      gender: value,
    }));
  };
  
  const handleSwitchChange = (name: string, checked: boolean) => {
    setFormData((prev) => ({
      ...prev,
      [name]: checked,
    }));
  };

  const handleEmergencyContactChange = (contacts: EmergencyContact[]) => {
    setFormData(prev => ({
      ...prev,
      emergency_contacts: contacts
    }));
  };

  const handleImageUpload = async (file: File) => {
    if (!file || !student?.id || !organizationId) return;
    
    setProfilePicture(file);
    
    try {
      const profilePictureUrl = await studentService.uploadProfilePicture(
        file, 
        organizationId, 
        student.id
      );
      
      // Update the form data with the new URL
      setFormData(prev => ({
        ...prev,
        profile_picture_url: profilePictureUrl
      }));
      
      toast.success("Profile picture uploaded");
    } catch (error) {
      console.error("Error uploading profile picture:", error);
      toast.error("Failed to upload profile picture");
    }
  };

  const saveStudent = async () => {
    if (!student?.id) return;
    
    setIsSubmitting(true);
    
    try {
      // First update the student record
      const { data, error } = await supabase
        .from("students")
        .update({
          first_name: formData.first_name,
          last_name: formData.last_name,
          preferred_name: formData.preferred_name,
          date_of_birth: formData.date_of_birth || null,
          gender: formData.gender || null,
          notes: formData.notes || null,
          address_line1: formData.address_line1 || null,
          address_line2: formData.address_line2 || null,
          city: formData.city || null,
          state: formData.state || null,
          zip: formData.zip || null,
          phone: formData.phone || null,
          email: formData.email || null,
          uses_parent_address: formData.uses_parent_address,
          parent_id_for_address: formData.parent_id_for_address || null,
          location_id: formData.location_id || null,
          profile_picture_url: formData.profile_picture_url || null,
          start_date: formData.start_date || null,
          drop_date: formData.drop_date || null,
          is_active: formData.is_active,
          no_public_photos: formData.no_public_photos
        })
        .eq("id", student.id)
        .select()
        .single();

      if (error) throw error;
      
      // Now handle emergency contacts
      await saveEmergencyContacts(student.id);

      console.log("Student updated:", data);
      toast.success("Student updated successfully");
      
      // Map the response to a Student object and call the onUpdated callback
      const updatedStudent = mapToStudent(data);
      onUpdated(updatedStudent);
    } catch (error) {
      console.error("Error updating student:", error);
      toast.error("Failed to update student");
    } finally {
      setIsSubmitting(false);
    }
  };
  
  const saveEmergencyContacts = async (studentId: string) => {
    try {
      // Filter out empty contacts - only save contacts with actual data
      const validContacts = formData.emergency_contacts.filter(contact => 
        (contact.name && contact.name.trim()) || 
        (contact.relationship && contact.relationship.trim()) || 
        (contact.phone && contact.phone.trim())
      );

      // First, get existing emergency contacts to determine which to update, add, or remove
      const { data: existingContacts, error: fetchError } = await supabase
        .from('emergency_contacts')
        .select('id')
        .eq('student_id', studentId);
      
      if (fetchError) throw fetchError;
      
      const existingIds = existingContacts?.map(contact => contact.id) || [];
      const currentIds = validContacts
        .filter(contact => contact.id)
        .map(contact => contact.id);
      
      // IDs to delete are in existingIds but not in currentIds
      const idsToDelete = existingIds.filter(id => !currentIds.includes(id));
      
      // Delete removed contacts
      if (idsToDelete.length > 0) {
        const { error: deleteError } = await supabase
          .from('emergency_contacts')
          .delete()
          .in('id', idsToDelete);
        
        if (deleteError) throw deleteError;
      }
      
      // Update or insert each valid contact
      for (const contact of validContacts) {
        if (contact.id) {
          // Update existing contact
          const { error: updateError } = await supabase
            .from('emergency_contacts')
            .update({
              name: contact.name,
              relationship: contact.relationship,
              phone: contact.phone,
              is_primary: contact.is_primary
            })
            .eq('id', contact.id);
          
          if (updateError) throw updateError;
        } else {
          // Insert new contact (only if it has valid data)
          const { error: insertError } = await supabase
            .from('emergency_contacts')
            .insert({
              student_id: studentId,
              name: contact.name,
              relationship: contact.relationship,
              phone: contact.phone,
              is_primary: contact.is_primary
            });
          
          if (insertError) throw insertError;
        }
      }
    } catch (error) {
      console.error("Error saving emergency contacts:", error);
      throw error; // Re-throw to be caught by the main saveStudent function
    }
  };

  return {
    formData,
    isSubmitting,
    handleChange,
    handleGenderChange,
    handleSwitchChange,
    handleEmergencyContactChange,
    handleImageUpload,
    saveStudent,
  };
};
